import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from './ScorecardNavigation';
import MonthFilter from './MonthFilter';
import ScorecardDropdown, { type DropdownOption } from '../common/ScorecardDropdown';
import LoadingSpinner from '../common/LoadingSpinner';
import { 
  managerScorecardData, 
  getAvailableMonths, 
  getLatestMonth,
  filterDataByMonth,
  type ManagerScorecardEntry 
} from '../../data/scorecardData';
import { config } from '../../config/environment';
import managementService, { ManagementUser } from '../../services/managementService';
import ShiftMetricsTable, { ShiftMetricsRow } from './ShiftMetricsTable';
import {
  getStoreShifts,
  type StoreShiftsGroup,
  ServerScorecardForbiddenError,
} from '../../services/serverScorecardService';
import { RoleBand } from './RoleGridHeading';
import { groupsOf, visibleGroups, showRoleHeadings } from '../../services/scorecardGroups';

const ManagerScorecard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedTenant, metricLabels } = useTenant();
  
  // Normalize role to handle case-insensitive comparison
  const normalizedRole = useMemo(() => {
    const rawRole = user?.managementUser?.role;
    if (!rawRole) return null;
    return rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase();
  }, [user?.managementUser?.role]);
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (!config.useDummyData) {
      // For real data environments (local, dev, prod), we'll set this after shifts are loaded
      return 'September 2025'; // Default fallback
    }
    return getLatestMonth(managerScorecardData);
  });
  const [selectedManagerId, setSelectedManagerId] = useState<string | null>(null);
  const [allManagers, setAllManagers] = useState<ManagementUser[]>([]);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  
  // Location selection state
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [availableLocations, setAvailableLocations] = useState<any[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  
  // Track if we've restored filters to prevent infinite loops
  const [hasRestoredFilters, setHasRestoredFilters] = useState(false);
  // Track the saved manager ID to prevent auto-selection from overriding it
  const savedManagerIdRef = useRef<string | null>(null);
  // Track if location selection has been initialized to prevent auto-selection after user selects "All Locations"
  const hasInitializedLocationRef = useRef<boolean>(false);
  // Track previous tenant to detect tenant changes
  const previousTenantRef = useRef<string | null>(null);

  // Determine location filter based on user role
  const getLocationFilter = useMemo(() => {
    if (config.useDummyData) {
      return undefined;
    }
    
    if (!user?.managementUser || !normalizedRole) {
      return undefined;
    }
    
    const { location_id } = user.managementUser;
    
    if (normalizedRole === 'Admin') {
      const filter = selectedLocation || undefined;
      console.log('ManagerScorecard: getLocationFilter (Admin) =', filter, 'selectedLocation =', selectedLocation);
      return filter;
    } else if (normalizedRole === 'Corporate') {
      const filter = selectedLocation || undefined;
      console.log('ManagerScorecard: getLocationFilter (Corporate) =', filter, 'selectedLocation =', selectedLocation);
      return filter;
    } else if (normalizedRole === 'Manager') {
      return location_id;
    }
    
    return undefined;
  }, [user?.managementUser, selectedLocation, normalizedRole]);

  // Determine tenant filter based on user role
  const getTenantFilter = useMemo(() => {
    if (config.useDummyData) {
      return undefined;
    }
    
    if (!user?.managementUser || !normalizedRole) {
      return undefined;
    }
    
    const { tenant_id } = user.managementUser;
    
    if (normalizedRole === 'Admin') {
      return selectedTenant || undefined;
    } else if (normalizedRole === 'Corporate') {
      return selectedTenant || tenant_id || undefined;
    } else if (normalizedRole === 'Manager') {
      return selectedTenant || tenant_id || undefined;
    }
    
    return undefined;
  }, [user?.managementUser, selectedTenant, normalizedRole]);

  // Restore filters when returning from details page
  useEffect(() => {
    const savedFilters = location.state?.filters;
    if (savedFilters && !hasRestoredFilters) {
      if (savedFilters.selectedMonth && savedFilters.selectedMonth !== selectedMonth) {
        setSelectedMonth(savedFilters.selectedMonth);
      }
      if (savedFilters.selectedLocation !== undefined && savedFilters.selectedLocation !== selectedLocation) {
        setSelectedLocation(savedFilters.selectedLocation);
        // Mark as initialized when restoring filters to prevent auto-selection
        hasInitializedLocationRef.current = true;
      }
      if (savedFilters.selectedManagerId !== undefined) {
        // Save the manager ID to a ref so we can check it later
        savedManagerIdRef.current = savedFilters.selectedManagerId;
        if (savedFilters.selectedManagerId !== selectedManagerId) {
          setSelectedManagerId(savedFilters.selectedManagerId);
        }
      }
      setHasRestoredFilters(true);
    } else if (!savedFilters && hasRestoredFilters) {
      // Reset the flag when there are no saved filters (new navigation)
      setHasRestoredFilters(false);
      savedManagerIdRef.current = null;
    }
  }, [location.state, hasRestoredFilters, selectedMonth, selectedLocation, selectedManagerId]);

  // Fetch locations for admin and corporate manager users
  useEffect(() => {
    const fetchLocations = async () => {
      if (!config.useDummyData && (normalizedRole === 'Admin' || normalizedRole === 'Corporate')) {
        // Use getTenantFilter to ensure Corporate managers always use their tenant
        const tenantId = getTenantFilter;
        
        // Check if tenant has changed - if so, reset initialization to allow auto-selection for new tenant
        const tenantChanged = previousTenantRef.current !== null && previousTenantRef.current !== tenantId;
        if (tenantChanged) {
          hasInitializedLocationRef.current = false;
        }
        previousTenantRef.current = tenantId || null;
        
        // For admin users, don't fetch if no tenant is selected
        if (normalizedRole === 'Admin' && !selectedTenant) {
          setAvailableLocations([]);
          setSelectedLocation(null);
          hasInitializedLocationRef.current = false; // Reset initialization when tenant is cleared
          return;
        }

        setIsLoadingLocations(true);
        setLocationsError(null);
        try {
          const response = await managementService.getLocationsByTenant(tenantId || undefined);
          if (response.success && response.locations) {
            setAvailableLocations(response.locations);
            setLocationsError(null);
            
            // If a location is selected but it's not in the new locations list (e.g., tenant changed),
            // reset to "All Locations" (null)
            // Only do this check when tenant changes, not when location selection changes
            if (tenantChanged && selectedLocation && !response.locations.find(loc => loc.id === selectedLocation)) {
              setSelectedLocation(null);
              hasInitializedLocationRef.current = false; // Reset to allow auto-selection for new tenant
            }
            
            // Auto-select location for corporate managers if they have only one location
            // Also auto-select if no location is currently selected and we have locations
            // Only auto-select on initial load or when tenant changes, not when user explicitly selects "All Locations"
            if (normalizedRole === 'Corporate' && response.locations.length === 1) {
              setSelectedLocation(response.locations[0].id);
              hasInitializedLocationRef.current = true;
            } else if (!hasInitializedLocationRef.current && response.locations.length > 0 && normalizedRole === 'Admin') {
              // For admins, auto-select first location only on initial load or tenant change
              setSelectedLocation(response.locations[0].id);
              hasInitializedLocationRef.current = true;
            } else if (!hasInitializedLocationRef.current) {
              // Mark as initialized even if we don't auto-select
              hasInitializedLocationRef.current = true;
            }
          } else if (response.error) {
            setLocationsError(response.error);
            setAvailableLocations([]);
          }
        } catch (error: any) {
          console.error('ManagerScorecard - Error fetching locations:', error);
          setLocationsError(error.message || 'Failed to load locations');
          setAvailableLocations([]);
        } finally {
          setIsLoadingLocations(false);
        }
      }
    };
    
    fetchLocations();
    // Removed selectedLocation from dependency array - we don't want to re-fetch locations when user changes location selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.managementUser, getTenantFilter, normalizedRole, selectedTenant]);

  // Fetch managers based on user role and selected filters
  useEffect(() => {
    const fetchManagers = async () => {
      if (!config.useDummyData && (normalizedRole === 'Admin' || normalizedRole === 'Corporate')) {
        // For admin users, don't fetch if no tenant is selected
        // Corporate users auto-use their tenant_id, so they can fetch without selecting
        if (normalizedRole === 'Admin' && !selectedTenant) {
          setAllManagers([]);
          setSelectedManagerId(null);
          return;
        }

        setIsLoadingManagers(true);
        try {
          console.log('ManagerScorecard - Fetching managers for role:', user?.managementUser?.role, 'selectedTenant:', selectedTenant, 'selectedLocation:', selectedLocation);
          const response = await managementService.getAllManagementUsers();
          console.log('ManagerScorecard - getAllManagementUsers response:', response);
          if (response.success && response.users) {
            console.log('ManagerScorecard - Total users from API:', response.users.length);
            console.log('ManagerScorecard - All users:', response.users.map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}`, role: u.role, tenant_id: u.tenant_id, location_id: u.location_id })));
            
            // Filter by role (case-insensitive)
            let managers = response.users.filter(manager => {
              const managerRole = manager.role?.toLowerCase();
              return managerRole === 'manager';
            });
            console.log('ManagerScorecard - After role filter (Manager):', managers.length, managers.map(m => ({ id: m.id, name: `${m.first_name} ${m.last_name}`, tenant_id: m.tenant_id, location_id: m.location_id })));
            
            // Filter by tenant using getTenantFilter
            const tenantFilter = getTenantFilter;
            console.log('ManagerScorecard - Tenant filter:', tenantFilter);
            if (tenantFilter) {
              const beforeTenantFilter = managers.length;
              managers = managers.filter(manager => manager.tenant_id === tenantFilter);
              console.log('ManagerScorecard - After tenant filter:', managers.length, 'was:', beforeTenantFilter, managers.map(m => ({ id: m.id, name: `${m.first_name} ${m.last_name}`, tenant_id: m.tenant_id })));
            }
            
            // Filter by location using getLocationFilter
            const locationFilter = getLocationFilter;
            console.log('ManagerScorecard - Location filter:', locationFilter);
            if (locationFilter) {
              const beforeLocationFilter = managers.length;
              managers = managers.filter(manager => manager.location_id === locationFilter);
              console.log('ManagerScorecard - After location filter:', managers.length, 'was:', beforeLocationFilter, managers.map(m => ({ id: m.id, name: `${m.first_name} ${m.last_name}`, location_id: m.location_id })));
            }
            
            console.log('ManagerScorecard - Found filtered managers:', managers);
            setAllManagers(managers);
            
            // Clear selected manager if it's no longer in the filtered list
            if (selectedManagerId && !managers.find(m => m.id === selectedManagerId)) {
              setSelectedManagerId(null);
            }
            
            // Set first manager as default selection if none is selected
            // But don't override if we're restoring from saved filters
            if (!selectedManagerId && managers.length > 0 && !savedManagerIdRef.current) {
              setSelectedManagerId(managers[0].id);
            } else if (savedManagerIdRef.current && managers.find(m => m.id === savedManagerIdRef.current)) {
              // If we have a saved manager ID and it's in the list, use it
              if (selectedManagerId !== savedManagerIdRef.current) {
                setSelectedManagerId(savedManagerIdRef.current);
              }
              // Clear the ref after using it
              savedManagerIdRef.current = null;
            }
          }
        } catch (error) {
          console.error('ManagerScorecard - Error fetching managers:', error);
        } finally {
          setIsLoadingManagers(false);
        }
      }
    };

    fetchManagers();
  }, [normalizedRole, getTenantFilter, getLocationFilter, selectedManagerId, selectedTenant]);

  // Determine which manager to show data for
  const currentManagerId = useMemo(() => {
    console.log('ManagerScorecard - currentManagerId calculation:', {
      useDummyData: config.useDummyData,
      user: user,
      managementUser: user?.managementUser,
      role: user?.managementUser?.role,
      selectedManagerId: selectedManagerId
    });
    
    if (!config.useDummyData && user?.managementUser && normalizedRole) {
      if (normalizedRole === 'Admin' || normalizedRole === 'Corporate') {
        // Admin and corporate manager users see selected manager's data
        console.log('ManagerScorecard - User is admin/corporate manager, using selectedManagerId:', selectedManagerId);
        return selectedManagerId;
      } else if (normalizedRole === 'Manager') {
        // Store managers see only their own data
        console.log('ManagerScorecard - User is store manager, using own ID:', user.managementUser.id);
        return user.managementUser.id;
      }
    }
    console.log('ManagerScorecard - No valid role found, returning null');
    return null;
  }, [user?.managementUser, selectedManagerId, normalizedRole]);

  // feat-093 Slice C: per-(server-shift) rows for the selected manager, sourced
  // from the backend GET /api/server-scorecard/{tenant}/shifts endpoint. Score,
  // visit_count and per-metric percents are all backend-computed and rendered
  // VERBATIM — the old client-side metric recompute (and its raw-SQL/Revel
  // fetches) is gone (feedback_metrics_single_source; AC-C3/C4).
  //
  // feat-317 (ADR §7.3): the response is one group per effective role, each
  // with its own metric_columns / drink_column / rows. An old backend (no
  // `groups`) synthesises one legacy group → today's single table.
  const [storeGroups, setStoreGroups] = useState<StoreShiftsGroup[]>([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  // Fetch backend-scored shift rows for the current manager's tenant/location.
  // The endpoint returns all server-shifts in the window; we filter to the
  // selected manager client-side (presentation grouping, not metric math).
  useEffect(() => {
    const fetchShifts = async () => {
      if (!config.useDummyData && currentManagerId) {
        const tenantId = getTenantFilter;
        if (!tenantId) {
          setStoreGroups([]);
          return;
        }
        setIsLoadingShifts(true);
        setHasAttemptedFetch(true);
        try {
          const resp = await getStoreShifts(tenantId, {
            locationId: getLocationFilter || undefined,
          });
          setStoreGroups(
            groupsOf(resp, (r): StoreShiftsGroup => ({
              role: null,
              display_name: null,
              metric_columns: r.metric_columns || [],
              drink_column: r.drink_column || null,
              rows: r.rows || [],
            })),
          );
        } catch (error) {
          if (error instanceof ServerScorecardForbiddenError) {
            // Fail-closed: tenant/location not available for this user.
            setStoreGroups([]);
          } else {
            console.error('ManagerScorecard - Error fetching shifts:', error);
            // Keep the previous columns (as before feat-317), drop the rows.
            setStoreGroups((prev) => prev.map((g) => ({ ...g, rows: [] })));
          }
        } finally {
          setIsLoadingShifts(false);
        }
      } else if (!config.useDummyData && !currentManagerId) {
        setStoreGroups([]);
        setHasAttemptedFetch(false);
        setIsLoadingShifts(false);
      }
    };

    fetchShifts();
  }, [currentManagerId, getTenantFilter, getLocationFilter]);

  // Normalize the backend rows for THIS manager into the render shape. Every
  // numeric field is verbatim from the backend — no React metric math.
  //
  // feat-317: rows of EVERY role group are normalised (each against its own
  // group's columns) into one list, tagged with `groupIndex`. The header tiles
  // read this whole list, so the month-to-date average stays ONE number across
  // all visible rows of all groups (spec round-3 Q1). The table splits it back
  // per group below, in response order.
  const scorecardData = useMemo(() => {
    if (!config.useDummyData) {
      return storeGroups.flatMap((group, groupIndex) => {
        const metricColumns = group.metric_columns || [];
        const drinkColumn = group.drink_column || null;
        const forManager = currentManagerId
          ? group.rows.filter((r) => r.manager_id === currentManagerId)
          : group.rows;
        return forManager.map((row) => {
          const metricValues: Record<string, number> = {};
          for (const col of metricColumns) {
            const cell = row.metrics[col.key];
            metricValues[col.key] = cell && cell.percent !== null && cell.percent !== undefined
              ? cell.percent
              : 0;
          }
          if (drinkColumn) {
            metricValues[drinkColumn.key] =
              row.drink && row.drink.percent !== null && row.drink.percent !== undefined
                ? row.drink.percent
                : 0;
          }
          return {
            date: row.shift_date,
            shift: row.shift_label,
            score: row.score ?? 0,
            // manager-shift id for the ShiftDetails drill-in
            shiftId: row.manager_shift_id || row.shift_id,
            created: row.shift_date,
            ended: undefined,
            serverName: row.server_name || '',
            // Visits — backend visit_count with the v:2 greetings fallback (nullish).
            visitCount: row.visit_count ?? row.greetings ?? undefined,
            greetings: row.greetings ?? 0,
            metricValues,
            groupIndex,
          };
        });
      });
    }
    // For dummy data environment, use dummy data
    return managerScorecardData;
  }, [storeGroups, currentManagerId]);

  // Get available months from data
  const availableMonths = useMemo(() => {
    if (!config.useDummyData) {
      const dataArray: any[] = Array.isArray(scorecardData) ? scorecardData : [];
      if (dataArray.length === 0) {
        return [];
      }

      // Extract unique months from the backend rows' shift_date (YYYY-MM-DD).
      const monthSet = new Set<string>();
      dataArray.forEach((entry: any) => {
        const shiftDate = new Date(entry.date);
        const monthString = shiftDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          timeZone: 'UTC',
        });
        monthSet.add(monthString);
      });

      // Sort months in descending order (most recent first)
      return Array.from(monthSet).sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateB.getTime() - dateA.getTime();
      });
    }

    // For dummy data environment, use dummy data
    return getAvailableMonths(scorecardData);
  }, [scorecardData]);

  // Set selected month to most recent when shifts are loaded
  useEffect(() => {
    if (!config.useDummyData && availableMonths.length > 0) {
      // Set to the most recent month (first in the sorted array)
      setSelectedMonth(availableMonths[0]);
    }
  }, [availableMonths]);

  // Helper function to parse selected month and get date range
  const getDateRangeForSelectedMonth = (monthString: string) => {
    try {
      // Handle "All" case - return null to indicate all months
      if (monthString === 'All') {
        return { year: null, month: null };
      }
      
      // Parse month string like "September 2025" or "2025-09"
      let year: number, month: number;
      
      if (monthString.includes('-')) {
        // Format: "2025-09"
        const parts = monthString.split('-');
        year = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
      } else {
        // Format: "September 2025"
        const date = new Date(monthString);
        year = date.getFullYear();
        month = date.getMonth();
      }
      
      return { year, month };
    } catch (error) {
      console.error('Error parsing month:', monthString, error);
      // Fallback to current month
      const now = new Date();
      return { year: now.getFullYear(), month: now.getMonth() };
    }
  };

  // Filter data by selected month
  const filteredData = useMemo(() => {
    let data: any[];
    if (!config.useDummyData) {
      // For real data, filter by selected month
      // Ensure scorecardData is treated as an array
      const dataArray: any[] = Array.isArray(scorecardData) ? scorecardData : [];

      if (selectedMonth === 'All') {
        data = dataArray;
      } else {
        // Filter by specific month
        const { year: selectedYear, month: selectedMonthNum } = getDateRangeForSelectedMonth(selectedMonth);
        data = dataArray.filter((entry: any) => {
          const entryDate = new Date(entry.date);
          return entryDate.getUTCMonth() === selectedMonthNum && entryDate.getUTCFullYear() === selectedYear;
        });
      }
    } else {
      // For dummy data, use centralized helper
      data = filterDataByMonth(scorecardData as any, selectedMonth);
    }
    // Sort by date descending
    return [...data].sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [scorecardData, selectedMonth]);

  const handleDetailsClick = (date: string, shift: string, score: number, shiftId?: string) => {
    console.log('ManagerScorecard - handleDetailsClick called with:', { date, shift, score, shiftId, currentManagerName, currentManagerId });
    navigate('/restaurant/manager-shift-details', { 
      state: { 
        shiftData: { 
          date, 
          shift, 
          score,
          managerName: currentManagerName,
          shiftId: shiftId || '',
          managerId: currentManagerId
        },
        // Save current filter state so we can restore it when coming back
        filters: {
          selectedMonth,
          selectedLocation,
          selectedManagerId
        }
      } 
    });
  };

  // Month-to-date display-average of the BACKEND per-shift scores (averaging
  // backend numbers is allowed under single-source; no rate is re-derived).
  const monthToDateScore = useMemo((): { score: string; shiftCount: number } => {
    if (!config.useDummyData) {
      const dataArray: any[] = Array.isArray(scorecardData) ? scorecardData : [];

      if (dataArray.length === 0) {
        return { score: "TBD", shiftCount: 0 };
      }

      // Handle "All" selection - calculate average across all shifts
      if (selectedMonth === 'All') {
        const totalScore = dataArray.reduce((sum: number, entry: any) => sum + (entry.score || 0), 0);
        const averageScore = totalScore / dataArray.length;
        return { score: `${Math.round(averageScore)}`, shiftCount: dataArray.length };
      }

      // Filter by selected month
      const { year: selectedYear, month: selectedMonthNum } = getDateRangeForSelectedMonth(selectedMonth);
      const selectedMonthEntries = dataArray.filter((entry: any) => {
        const entryDate = new Date(entry.date);
        return entryDate.getUTCMonth() === selectedMonthNum && entryDate.getUTCFullYear() === selectedYear;
      });

      if (selectedMonthEntries.length === 0) {
        return { score: "TBD", shiftCount: 0 };
      }

      const totalScore = selectedMonthEntries.reduce((sum: number, entry: any) => sum + (entry.score || 0), 0);
      const averageScore = totalScore / selectedMonthEntries.length;

      return { score: `${Math.round(averageScore)}`, shiftCount: selectedMonthEntries.length };
    }

    // For dummy data environment, use filtered data
    if (filteredData.length === 0) return { score: "0", shiftCount: 0 };
    const scoresWithValues = filteredData.filter((entry: any) => entry.score > 0);
    if (scoresWithValues.length === 0) return { score: "0", shiftCount: 0 };
    const avg = Math.round(
      scoresWithValues.reduce((sum: number, entry: any) => sum + entry.score, 0) / scoresWithValues.length
    );
    return { score: `${avg}`, shiftCount: scoresWithValues.length };
  }, [scorecardData, filteredData, selectedMonth]);

  // Most recent shift's backend score, rendered verbatim.
  const mostRecentScore = useMemo(() => {
    if (!config.useDummyData) {
      const dataArray: any[] = Array.isArray(scorecardData) ? scorecardData : [];

      if (dataArray.length === 0) {
        return "TBD";
      }

      // Get the most recent entry by date
      const sorted = [...dataArray].sort((a: any, b: any) =>
        new Date(b.created || b.date).getTime() - new Date(a.created || a.date).getTime()
      );
      const mostRecent = sorted[0];
      if (mostRecent && mostRecent.score !== undefined && mostRecent.score !== null) {
        return `${Math.round(mostRecent.score)}`;
      }

      return "TBD";
    }

    // For dummy data environment, use dummy data
    return "92%";
  }, [scorecardData]);

  // Get current manager name based on environment and role
  const currentManagerName = useMemo(() => {
    console.log('ManagerScorecard - Environment:', config.environment);
    console.log('ManagerScorecard - User:', user);
    console.log('ManagerScorecard - User managementUser:', user?.managementUser);
    console.log('ManagerScorecard - CurrentManagerId:', currentManagerId);
    console.log('ManagerScorecard - AllManagers:', allManagers);
    
    if (!config.useDummyData && normalizedRole) {
      // For store managers, show their own name
      if (normalizedRole === 'Manager') {
        if (user?.managementUser) {
          const fullName = managementService.getFullName(user.managementUser.first_name, user.managementUser.last_name);
          console.log('ManagerScorecard - Using own name:', fullName);
          return fullName;
        }
      }
      
      // For admin and corporate manager users, show selected manager's name
      if (normalizedRole === 'Admin' || normalizedRole === 'Corporate') {
        if (currentManagerId && allManagers.length > 0) {
          const selectedManager = allManagers.find(manager => manager.id === currentManagerId);
          if (selectedManager) {
            const fullName = managementService.getFullName(selectedManager.first_name, selectedManager.last_name);
            console.log('ManagerScorecard - Using selected manager name:', fullName);
            return fullName;
          }
        }
        console.log('ManagerScorecard - No selected manager found, showing placeholder');
        return 'Select a manager';
      }
      
      // Fallback for real data environments when user data isn't available
      console.log('ManagerScorecard - No user data available');
      return 'Manager';
    }
    
    // For dummy data environment, use dummy data
    console.log('ManagerScorecard - Using dummy name: Sarah Johnson');
    return 'Sarah Johnson';
  }, [user, currentManagerId, allManagers, normalizedRole]);

  // Check if user can select managers (admin or corporate manager)
  const canSelectManager = normalizedRole === 'Admin' || normalizedRole === 'Corporate';

  // Convert managers to dropdown options
  const managerOptions: DropdownOption[] = useMemo(() => {
    const options = allManagers.map(manager => ({
      value: manager.id,
      label: managementService.getFullName(manager.first_name, manager.last_name)
    }));
    
    // Add placeholder option at the beginning
    return [
      { value: '', label: 'Select a manager' },
      ...options
    ];
  }, [allManagers]);
  
  // Debug logging for dropdown display
  console.log('ManagerScorecard - Dropdown display debug:', {
    canSelectManager,
    useDummyData: config.useDummyData,
    userRole: user?.managementUser?.role?.toLowerCase(),
    selectedTenant,
    selectedLocation,
    allManagersCount: allManagers.length,
    managerOptionsCount: managerOptions.length,
    selectedManagerId,
    isLoadingManagers,
    allManagers: allManagers.map(m => ({ id: m.id, name: `${m.first_name} ${m.last_name}`, tenant_id: m.tenant_id, location_id: m.location_id }))
  });

  // Determine if we should show loading state
  const isDataLoading = useMemo(() => {
    if (config.useDummyData) {
      return false;
    }

    // For admin/corporate users, check if managers or locations are loading
    if (normalizedRole === 'Admin' || normalizedRole === 'Corporate') {
      // If we need a tenant but don't have one, don't show loading (will show tenant selection message)
      if (normalizedRole === 'Admin' && !selectedTenant) {
        return false;
      }
      
      // Check if managers or locations are still loading
      if (isLoadingManagers || isLoadingLocations) {
        return true;
      }
      
      // If we need a manager but don't have one selected yet, don't show loading
      if (canSelectManager && !selectedManagerId) {
        return false;
      }
    }
    
    // Check if shifts are currently loading
    if (isLoadingShifts) {
      return true;
    }
    
    // If we have a manager ID but haven't attempted to fetch shifts yet, show loading
    // This handles the initial state before the useEffect runs
    if (currentManagerId && !hasAttemptedFetch) {
      return true;
    }
    
    // If we have a manager ID, attempted to fetch, but have no shifts and we're not loading,
    // that means we've finished loading with no data - don't show loading spinner
    // (will show empty state message instead)
    
    return false;
  }, [config.useDummyData, normalizedRole, selectedTenant, isLoadingManagers, isLoadingLocations, isLoadingShifts, canSelectManager, selectedManagerId, currentManagerId, hasAttemptedFetch]);

  // Show loading spinner while data is being fetched
  if (isDataLoading && !config.useDummyData) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <LoadingSpinner message="Loading manager data..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <ScorecardNavigation />
      
      <div className="p-6">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <h1 className="text-4xl font-display font-semibold text-ink">Manager Scorecard</h1>
          <div className="flex items-center space-x-4">
            {/* Location Selector for Admin and Corporate Manager */}
            {(() => {
              const isAdminOrCorporate = normalizedRole === 'Admin' || normalizedRole === 'Corporate';
              return isAdminOrCorporate && !config.useDummyData ? (
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <label htmlFor="location-select" className="text-sm font-medium text-gray-700">
                      Location:
                    </label>
                    <select
                      id="location-select"
                      value={selectedLocation || ''}
                      onChange={(e) => {
                        const newLocation = e.target.value || null;
                        console.log('ManagerScorecard: Location changed to:', newLocation);
                        // Mark as initialized when user makes a selection
                        hasInitializedLocationRef.current = true;
                        setSelectedLocation(newLocation);
                      }}
                      className={`px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        locationsError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      disabled={isLoadingLocations || !!locationsError}
                    >
                      <option value="">All Locations</option>
                      {availableLocations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                    {isLoadingLocations && (
                      <div className="text-sm text-gray-500">Loading...</div>
                    )}
                  </div>
                  {locationsError && (
                    <div className="text-xs text-red-600 mt-1">
                      {locationsError}
                    </div>
                  )}
                </div>
              ) : null;
            })()}
            
            <MonthFilter
              availableMonths={availableMonths}
              selectedMonth={selectedMonth}
              onMonthChange={setSelectedMonth}
              showAllOption={true}
            />
          </div>
        </div>

      {/* Stats Blocks */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        {/* Manager Block */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
            Manager
          </div>
          {canSelectManager && !config.useDummyData ? (
            <>
              {normalizedRole === 'Admin' && !selectedTenant ? (
                <div className="text-lg text-gray-600">
                  Please select a tenant first
                </div>
              ) : (
                <ScorecardDropdown
                  value={selectedManagerId || ''}
                  onChange={setSelectedManagerId}
                  options={managerOptions}
                  loading={isLoadingManagers}
                  loadingText="Loading managers..."
                  variant="header"
                />
              )}
            </>
          ) : (
            <div className="text-2xl font-bold text-gray-900">
              {currentManagerName}
            </div>
          )}
        </div>

        {/* Month to Date Score Block */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
            {selectedMonth === 'All' ? 'Overall Average Score' : 'Month to Date Score'}
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {monthToDateScore.score}
          </div>
          {monthToDateScore.shiftCount > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Average over {monthToDateScore.shiftCount} shift{monthToDateScore.shiftCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Most Recent Shift Score Block */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
            Most Recent Shift Score
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {mostRecentScore}
          </div>
        </div>
      </div>

      {/* Table Container — feat-093 Slice C: one backend-scored table. Columns,
          score, visit_count and per-metric percents are all backend-sourced and
          rendered VERBATIM (zero client-side metric math). */}
      {(() => {
        // For dummy data the shape is the legacy ManagerScorecardEntry (no
        // metricValues/visitCount); the real path carries backend percents.
        const buildRow = (row: any): ShiftMetricsRow => ({
          date: row.date,
          serverName: row.serverName,
          type: row.shift,
          // Visits — backend visit_count (?? greetings), nullish so a real 0 shows 0.
          greetings: row.greetings ?? 0,
          visitCount: row.visitCount ?? row.greetings ?? undefined,
          score: row.score ?? 0,
          premiumMenu: 0,
          itemMention: 0,
          itemDescription: 0,
          drinkPromotions: 0,
          shiftId: row.shiftId,
          metricValues: row.metricValues || {},
        });

        const shiftsLabel = selectedMonth === 'All' ? 'All Shifts' : 'Shifts This Month';

        // feat-317: split the month-filtered, date-sorted rows back into their
        // role groups (response order, rows keep today's sort), then drop a
        // group the manager/month filters emptied. All empty → the first group,
        // emptied, so today's empty table renders with its columns.
        const tableGroups = config.useDummyData
          ? []
          : visibleGroups(
              storeGroups.map((group, i) => ({
                group,
                rows: (filteredData || []).filter((r: any) => r.groupIndex === i),
              })),
              (t) => t.rows,
            );

        const renderTable = (
          rows: any[],
          group: StoreShiftsGroup | undefined,
        ) => (
          <ShiftMetricsTable
            // feat-297: pinned header; the card above is overflow-clip (not -hidden) so the sticky reaches the page.
            pinHeader
            shifts={rows.map(buildRow)}
            showDate={true}
            showServer={true}
            showGreetings={true}
            onViewDetails={(row) => {
              handleDetailsClick(row.date || '', row.type, row.score, row.shiftId);
            }}
            emptyMessage="No shift data available for the selected month."
            metricLabels={metricLabels}
            dynamicMetrics={config.useDummyData ? undefined : (group?.metric_columns || []).map((c) => ({ key: c.key, label: c.label } as any))}
            dynamicDrinkLabel={config.useDummyData ? undefined : (group?.drink_column ? group.drink_column.label : null)}
          />
        );

        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-clip mb-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                {`Scores from ${shiftsLabel}`}
              </h2>
            </div>
            {showRoleHeadings(tableGroups) ? (
              // feat-317 (design NOTES §4.4): one role band + table per group
              // inside the one card. Each group's wrapper div is the containing
              // block that releases its pinned header at the end of its group.
              <div>
                {tableGroups.map((t, i) => (
                  <div key={t.group.role ?? `group-${i}`}>
                    <RoleBand displayName={t.group.display_name} role={t.group.role} first={i === 0} />
                    {renderTable(t.rows, t.group)}
                  </div>
                ))}
              </div>
            ) : (
              renderTable(
                config.useDummyData ? filteredData || [] : tableGroups[0]?.rows ?? [],
                tableGroups[0]?.group,
              )
            )}
          </div>
        );
      })()}

      {/* Empty State */}
      {scorecardData.length === 0 && (
        <div className="text-center py-12">
          {isLoadingShifts ? (
            <div className="text-gray-500 text-lg">Loading shifts...</div>
          ) : !selectedManagerId && canSelectManager ? (
            <>
              <div className="text-gray-500 text-lg">Select a manager to view their scorecard</div>
              <div className="text-gray-400 text-sm mt-2">
                Choose a manager from the dropdown above to see their performance data
              </div>
            </>
          ) : (
            <>
              <div className="text-gray-500 text-lg">No shifts found</div>
              <div className="text-gray-400 text-sm mt-2">
                {!config.useDummyData
                  ? 'No shifts have been assigned to this manager yet'
                  : 'Check back later for manager performance data'
                }
              </div>
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default ManagerScorecard;
